import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { useToast } from '~/lib/ui/toast'
import { useServiceRequest } from './ServiceRequestDetail'
import { useCandidate, queryClient } from './CandidateProvider'
import { InlineWidget, useCalendlyEventListener } from 'react-calendly'
import { useMutation } from '@tanstack/react-query'
import {
  calendlyEventMutation,
  updateLocationDetailItemId,
} from './calendlyMutations'

const RescheduleMeetingDialog = ({ children }) => {
  const [open, setOpen] = useState(false)
  const serviceRequest = useServiceRequest()
  const {
    candidateId,
    coachCalendlyLink,
    candidateName,
    candidateEmail,
    coachId,
    idRef,
  } = useCandidate()
  const { toast } = useToast()
  const requestType = serviceRequest?.requestType
  const displayName = serviceRequest?.displayName || 'Resume Review'
  const thirtyMinTypes = [
    'ServiceRequest::NetworkingStrategy',
    'ResumeRevision::CandidateRevision',
    'ServiceRequest::ExecutiveBio',
    'ServiceRequest::RecruiterGuidanceSupport',
    'ServiceRequest::BoardSearch',
    'ServiceRequest::JobSearchBurnout',
    'ServiceRequest::ResumeTailoring',
    'ServiceRequest::ResumeWriting',
    'ServiceRequest::NegotiationStrategy',
    'ServiceRequest::CoverLetterReview',
    'ServiceRequest::OnboardingSupport',
  ]
  const firstOnboardingSupport =
    requestType === 'ServiceRequest::OnboardingSupport'
  const meetingDuration =
    thirtyMinTypes.includes(requestType) && !firstOnboardingSupport ? 30 : 50

  const createCalendlyEventMutation = useMutation({
    mutationFn: (payload) =>
      calendlyEventMutation({
        payload,
        candidateId,
        coachId,
        serviceRequestId: serviceRequest.id,
        requestType,
      }),
    onSuccess: (response) => {
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      const newId = response?.service_request?.id
      if (idRef && newId) {
        idRef.current = newId
      }
      if (newId) updateLocationDetailItemId(newId)
      toast({
        title: 'Success',
        description: `${displayName} meeting rescheduled`,
      })
    },
    onError: (error) => {
      console.error(`Error rescheduling ${displayName} meeting:`, error)
      toast({
        title: 'Error',
        description: `Failed to reschedule ${displayName} meeting`,
        variant: 'destructive',
      })
    },
  })

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      const payload = e.data.payload
      createCalendlyEventMutation.mutate(payload)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen} className="rounded-2xl">
      <DialogTrigger asChild className="cursor-pointer">
        {children}
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-xl xl:max-w-3xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle>Reschedule Meeting for {displayName}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          <InlineWidget
            url={`${coachCalendlyLink}/${meetingDuration}min`}
            prefill={{
              name: candidateName,
              email: candidateEmail,
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RescheduleMeetingDialog
